// ==========================================================================
// OPTION INFO HOVER — Dutton Brown
// Body-portal tooltip for the ⓘ info icons next to variant-picker option
// labels (rendered by snippets/option-info-hover.liquid).
//
// Why a body portal: the trigger sits inside .variant-picker__option-info,
// which lives in a column with overflow/scroll containers. A CSS pseudo-element
// bubble would get clipped. Rendering the bubble as a single <div> on <body>
// and positioning it with getBoundingClientRect avoids any ancestor clipping —
// same approach as the swatch tooltip in collection-controls.js.
//
// Triggers: hover (mouse), focus (keyboard), and tap (touch). On touch the
// bubble toggles and dismisses on outside-tap. Repositions/hides on scroll.
// ==========================================================================

(function () {
  "use strict";

  const SELECTOR = ".option-info-hover";
  const MARGIN = 8; // min gap from viewport edge

  let bubble = null;
  let activeTrigger = null;

  function ensureBubble() {
    if (bubble) return bubble;
    bubble = document.createElement("div");
    bubble.className = "db-info-bubble";
    bubble.setAttribute("role", "tooltip");
    bubble.setAttribute("aria-hidden", "true");
    document.body.appendChild(bubble);
    return bubble;
  }

  function show(trigger) {
    const text = trigger.getAttribute("data-option-info");
    if (!text) return;

    const el = ensureBubble();
    // Copy may carry a "|||" paragraph delimiter (see option-info-hover.liquid).
    // Render each part as its own <p> using textContent per node so the copy
    // stays escaped — no innerHTML, no markup injection.
    el.textContent = "";
    text.split("|||").forEach((part) => {
      const trimmed = part.trim();
      if (!trimmed) return;
      const p = document.createElement("p");
      p.className = "db-info-bubble__p";
      p.textContent = trimmed;
      el.appendChild(p);
    });
    el.setAttribute("aria-hidden", "false");
    activeTrigger = trigger;

    // Make it measurable before positioning.
    el.style.left = "0px";
    el.style.top = "0px";
    el.classList.add("is-visible");

    position(trigger);
  }

  function position(trigger) {
    if (!bubble || activeTrigger !== trigger) return;
    const rect = trigger.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();

    // Default: centered above the icon (transform: translate(-50%, -100%)).
    let left = rect.left + rect.width / 2;
    const top = rect.top - 6;

    // Clamp horizontally so the bubble never spills off-screen. Because the
    // bubble is translated -50%, its visual edges are at left ± halfWidth.
    const halfWidth = bubbleRect.width / 2;
    const minLeft = MARGIN + halfWidth;
    const maxLeft = window.innerWidth - MARGIN - halfWidth;
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = maxLeft;

    bubble.style.left = left + "px";
    bubble.style.top = top + "px";
  }

  function hide() {
    if (!bubble) return;
    bubble.classList.remove("is-visible");
    bubble.setAttribute("aria-hidden", "true");
    activeTrigger = null;
  }

  // ---- Mouse ----
  document.addEventListener("mouseover", (e) => {
    const trigger = e.target.closest && e.target.closest(SELECTOR);
    if (trigger && trigger !== activeTrigger) show(trigger);
  });
  document.addEventListener("mouseout", (e) => {
    if (!activeTrigger) return;
    const rt = e.relatedTarget;
    if (rt && rt.closest && rt.closest(SELECTOR) === activeTrigger) return;
    hide();
  });

  // ---- Keyboard ----
  document.addEventListener("focusin", (e) => {
    const trigger = e.target.closest && e.target.closest(SELECTOR);
    if (trigger) show(trigger);
  });
  document.addEventListener("focusout", (e) => {
    const trigger = e.target.closest && e.target.closest(SELECTOR);
    if (trigger && trigger === activeTrigger) hide();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && activeTrigger) {
      activeTrigger.blur();
      hide();
    }
  });

  // ---- Touch ----
  // Toggle on tap; the button's click handler runs, so we toggle here and
  // suppress the synthetic focus behavior from re-showing it.
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest && e.target.closest(SELECTOR);
    if (trigger) {
      e.preventDefault();
      if (activeTrigger === trigger) {
        hide();
      } else {
        show(trigger);
      }
      return;
    }
    // Outside tap dismisses an open bubble.
    if (activeTrigger) hide();
  });

  // ---- Keep position sane ----
  window.addEventListener(
    "scroll",
    () => {
      if (activeTrigger) hide();
    },
    { passive: true, capture: true }
  );
  window.addEventListener("resize", () => {
    if (activeTrigger) position(activeTrigger);
  });
})();
